/**
 * Drawer Open Handlers for Native Interactive Site Map (ISM)
 *
 * Pure DOM observation — no Redux, no CSS mutation, no business logic. Reports when the shared
 * layout drawer (`[data-drawer]`) transitions from collapsed/peek to fully open, firing once per
 * open. Opening is triggered by a tap (not a drag), so it never surfaces through
 * handlers/drawerDragHandlers.js — this observer captures the *outcome* (the drawer became open)
 * regardless of whether a tap, a drag-up, or a programmatic change caused it, which is the most
 * reliable "drawer opened" signal available without touching the shared drawer.
 *
 * The open state is read from the `data-drawer-open` attribute that layout/components/drawer.js
 * keeps in sync with its expanded state — a stable hook exposed for exactly this, so we observe it
 * rather than coupling to the internal `nhs-drawer--open` class name. Read-only, the same way the
 * plugin already reads `[data-drawer]` itself. Mobile-only gating is the caller's responsibility
 * (see plugins/tracking.plugin.js).
 *
 * @module nativeInteractiveSiteMap/handlers/drawerOpenHandlers
 */

// Attribute layout/components/drawer.js sets to 'true' when the drawer is fully expanded.
const OPEN_ATTRIBUTE = 'data-drawer-open';

function createDrawerOpenHandlers() {
  const state = {
    observer: null,
  };

  const isElementOpen = (element) => element.getAttribute(OPEN_ATTRIBUTE) === 'true';

  /**
   * @param {HTMLElement} element - the drawer element to observe (e.g. `[data-drawer]`)
   * @param {() => void} onOpened - invoked once each time the drawer transitions to open
   */
  function bind(element, onOpened) {
    // Seed from the current state so binding while already open doesn't fire a spurious event.
    let wasOpen = isElementOpen(element);

    state.observer = new MutationObserver(() => {
      const isOpen = isElementOpen(element);
      if (isOpen && !wasOpen) {
        onOpened();
      }
      wasOpen = isOpen;
    });

    state.observer.observe(element, { attributes: true, attributeFilter: [OPEN_ATTRIBUTE] });
  }

  function unbind() {
    state.observer?.disconnect();
    state.observer = null;
  }

  return { bind, unbind };
}

export default createDrawerOpenHandlers;
